/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import util from "../../../../share/util/utils";
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  WAIT: 'wait', // 待機
  LEARNING_SENSOR: 'learningSensor', // センサー類学習中の表示
  LEARNING_CURRENT: 'learningCurrent', // リニア弁開弁電流値学習中の表示
  SUCCESS: 'success', // 一括学習完了表示
  INCORRECT: 'incorrect', // 実行条件不成立表示
  ERROR: 'error', // 共通エラー
};

export default {
  name: 'requirement144',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      modal_requirement_144: false,
      systemId: '',
      isDispose: false, // Dispose処理用フラグ
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.INCORRECT:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.WAIT:
        case PHASE.LEARNING_SENSOR:
        case PHASE.LEARNING_CURRENT:
        default:
          return 'secondary';
      }
    },

    modalSize() {
      switch (this.phase) {
        case PHASE.SUCCESS:
        case PHASE.WAIT:
          return 'lg';
        default:
          return 'md';
      }
    },

    headerTitle() {
      // 待機, センサー類学習中の表示
      const confirmationTitle = 'message.common_title_confirmation';
      // リニア弁開弁電流値学習中の表示
      const executionTitle = 'individual.common_title_execution';
      // 一括学習完了表示
      const successTitle = 'message.common_title_success';
      // 実行条件不成立表示, 共通エラー
      const errorTitle = 'message.common_title_error';
      const titleMap = {
        [PHASE.WAIT]: confirmationTitle,
        [PHASE.LEARNING_SENSOR]: confirmationTitle,
        [PHASE.LEARNING_CURRENT]: executionTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.INCORRECT]: errorTitle,
        [PHASE.ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        [PHASE.WAIT]: `${this.i18nWsKey}.REQ_WS_144_TXT_1`,
        [PHASE.LEARNING_SENSOR]: `${this.i18nWsKey}.REQ_WS_144_TXT_2`,
        [PHASE.LEARNING_CURRENT]: `${this.i18nWsKey}.REQ_WS_144_TXT_3`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_144_TXT_4`,
        [PHASE.INCORRECT]: `${this.i18nWsKey}.REQ_WS_144_TXT_5`,
        [PHASE.ERROR]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.SUCCESS]: true,
        [PHASE.INCORRECT]: true,
        [PHASE.ERROR]: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.WAIT]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_144 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_144 = false;
    },

    async onOkClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          await this.execute();
          break;
        case PHASE.SUCCESS:
        case PHASE.INCORRECT:
        case PHASE.ERROR:
          await this.exit();
          break;
        default:
          break;
      }
    },

    /**
     * 実行処理
     */
    async execute() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            // センサー類学習中の表示
            await this.learningSensorProcess();
            break;
          case REQUEST_STATUS_FAILED:
            if (response.data.user_data && typeof response.data.user_data.is_dispose === 'boolean') {
              this.isDispose = response.data.user_data.is_dispose;
            }
            switch (response.data.async_code) {
              case error.WSP_144_EXECUTE_FAILED:
                // 実行条件不成立表示へ遷移
                this.phase = PHASE.INCORRECT;
                break;
              default:
                // 共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * センサー類学習中の表示処理
     */
    async learningSensorProcess() {
      // センサー類学習中の表示へ遷移
      this.phase = PHASE.LEARNING_SENSOR;
      await util.sleep(5000);
      await this.learningCurrentProcess();
    },

    /**
     * リニア弁開弁電流値学習中の表示処理
     */
    async learningCurrentProcess() {
      // リニア弁開弁電流値学習中の表示へ遷移
      this.phase = PHASE.LEARNING_CURRENT;
      await util.sleep(120000);
      await this.disableFactoryMode();
    },

    /**
     * 工場モード解除処理
     */
    async disableFactoryMode() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            // 一括学習完了表示へ遷移
            this.phase = PHASE.SUCCESS;
            break;
          case REQUEST_STATUS_FAILED:
            // 異常終了
            switch (response.data.async_code) {
              case error.WSP_144_EXECUTE_FAILED:
                // 実行条件不成立表示へ遷移
                this.phase = PHASE.INCORRECT;
                break;
              default:
                // 共通エラー
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * exit処理
     */
    async exit() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: this.isDispose ? 'dispose' : 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 結果に関わらず機能終了する
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  async mounted() {
    this.init();
  },
};